import React, { FC, useEffect, useState } from "react";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import FormInput from "../../form-components/InputComponent";
import { SelectOption } from "../../form-components/SelectComponent";
import FormDatePicker from "../../form-components/DatePicker";
import {
  Alert,
  Button,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  UncontrolledDropdown,
} from "reactstrap";
import {
  // CheckCircleOutlined,
  LoadingOutlined,
  PlusSquareOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { pmsMasterApis } from "../../../api/PmsMaster";
import { ratecardApis } from "../../../api/Ratecard";
import Swal from "sweetalert2";
import { ReactTyped } from "react-typed";
import { Descriptions, Flex, Popconfirm, Result, Spin, Table, Tag } from "antd";
import { nanoid } from "@reduxjs/toolkit";
import { useSelector } from "react-redux";
import RateCardWageModal from "./RateCardWageModal";
import { Icon } from "../../../components/Component";
import { ApiResponse } from "../../../api/master/axiosIndex";
import { formatCurrency } from "../../../utils/Utils";
import { CustomJsonParse } from "../../../utils/convertions";
import {
  EditAccess,
  ViewAccess,
} from "../../../components/accessControl/AccessControl";

export type LaborFlagType = "My Labor" | "Vendor Labor";
export const LaborFlagOptions = [
  { value: "My Labor", label: "My Labor" },
  { value: "Vendor Labor", label: "Vendor Labor" },
];

export type RateCardPaymentType = "Daily" | "Monthly" | "Hourly";
export const RateCardPaymentTypeOptions = [
  { value: "Hourly", label: "Hourly" },
  { value: "Daily", label: "Daily" },
  { value: "Monthly", label: "Monthly" },
];

export type WagesType = {
  wageId?: number;
  laborFlag: LaborFlagType;

  paymentType: RateCardPaymentType;
  paymentDate?: any;

  laborTypeId: number | undefined;
  laborTypeName?: string | undefined;
  laborCategoryId: number | undefined;
  laborCategoryName?: string | undefined;

  baseHourlyRate?: number;
  overTimeRate?: number;
  weekendRate?: number;
  nightShiftRate?: number;
  dayRate?: number;
  monthlyRate?: number;
  currencyId?: number;
  currencyCode?: string;
};

export interface RatecardSubmit {
  rateCardTitle: string;
  validityStartDate: any;
  validityEndDate: any;
  wages: Array<WagesType>;
}
export interface CurrencyData {
  description: string;
  currency_code: string;
  currency_symbol: string;
  currency_format: string;
  example: string;
  id: any;
}

export type RateCardFetchApiResponse = {
  rateCardId: Number;
  orgId: Number;
  rateCardTitle: String;
  validityStartDate: any;
  validityEndDate: any;
  status: "Active" | "Inactive";
  createdAt: any;
  updatedAt: any;
  createdBy: Number;
  updatedBy: Number;
  createdByUserName: String;
  updatedByUserName: String;
  rateCardWages: WagesType[];
};

const RateCardCreation: FC<{
  id: any;
  close: () => void;
  refresh: () => void;
  extra?: {
    defaultValues: RatecardSubmit;
  };
}> = ({ id, close, refresh, extra }) => {
  const [loading, setLoading] = useState(id ? true : false);
  const [originalData, setOriginalData] = useState<RateCardFetchApiResponse>();
  const [mode, setMode] = useState<"create" | "edit" | "view">(
    id ? "view" : "create"
  );
  const [screenSize, setScreenSize] = useState(0);
  const [labourTypesList, setLabourTypesList] = useState<SelectOption[]>([]);
  const [laborCatgoriesList, setLaborCatgoriesList] = useState<SelectOption[]>(
    []
  );
  const { data: SideNavCollapse } = useSelector(
    (state: any) => state.SideNavCollapseStatus
  );
  const [allCurrencyData, setAllCurrencyData] = useState<CurrencyData[]>([]);

  const [warningWage, setWarningWage] = useState<{
    error: any | undefined;
    message: string | undefined;
  }>({
    error: undefined,
    message: undefined,
  });

  const [wageModalStatus, setWageModalStatus] = useState<{
    open: boolean;
    title: "Add New Labor" | "Edit Labor";
    data: WagesType;
    index: number | undefined;
  }>({
    open: false,
    title: "Add New Labor",
    data: {
      laborFlag: "My Labor",
      laborTypeId: undefined,
      laborTypeName: undefined,
      laborCategoryId: undefined,
      laborCategoryName: undefined,
      paymentType: "Hourly",
      paymentDate: "",
      baseHourlyRate: undefined,
      overTimeRate: undefined,
      weekendRate: undefined,
      nightShiftRate: undefined,
      dayRate: undefined,
      monthlyRate: undefined,
      currencyId: 3, // INR
      currencyCode: "INR",
    },
    index: undefined,
  });

  const methods = useForm<RatecardSubmit>({
    mode: "onChange",
    // defaultValues: {
    //   ...extra?.defaultValues,
    // },
  });

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    // reset,
    // formState: { errors, dirtyFields },
  } = methods;
  const { fields, append, update, remove } = useFieldArray({
    control,
    name: "wages",
  });
  const [deletedWages, setDeletedWages] = useState<number[]>([]);
  const [updatedWages, setUpdatedWages] = useState<{
    [key: number]: WagesType;
  }>({});

  const validityStartDateWatch: any = watch("validityStartDate");
  const validityEndDateWatch: any = watch("validityEndDate");

  const validDays =
    validityEndDateWatch && validityStartDateWatch
      ? dayjs(validityEndDateWatch).diff(dayjs(validityStartDateWatch), "day")
      : 0;

  const resetWarningWage = () => {
    setWarningWage({
      error: undefined,
      message: undefined,
    });
  };

  const resettingFields = (originalData: any) => {
    try {
      resetWarningWage();
      const { validityStartDate, validityEndDate, rateCardWages } =
        originalData;
      setValue("validityEndDate", dayjs(validityEndDate));
      setValue("validityStartDate", dayjs(validityStartDate));
      setValue(
        "wages",
        rateCardWages?.map((item: any) => ({
          ...item,
          id: item.wageId,
          currencyCode: item?.currency?.currencyCode,
        })) as WagesType[]
      );
      setDeletedWages([]);
      setUpdatedWages({});
      setLoading(false);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchLabourTypesList = async () => {
    try {
      const response = await pmsMasterApis.labourTypesList();
      const { data: optionsArray, status } = response;
      if (status) {
        setLabourTypesList(
          optionsArray?.map((item: any) => ({
            value: item?.laborTypeId,
            label: item?.laborType,
          }))
        );
      }
    } catch (error) {
      console.log(error);
    }
  };
  const fetchLabourCatgoriesList = async () => {
    try {
      const response = await pmsMasterApis.labourCatgoriesList();
      const { data: optionsArray, status } = response;
      if (status) {
        setLaborCatgoriesList(
          optionsArray?.map((item: any) => ({
            value: item?.laborCategoryId,
            label: item?.laborCategory,
          }))
        );
      }
    } catch (error) {
      console.log(error);
    }
  };
  const fetchCurrencyData = async () => {
    try {
      const res = await pmsMasterApis.getCurrencyTypes();
      const { data, status } = res;
      if (status) {
        let allCurrencyData = data.map((item: any) => {
          return {
            description: item.description,
            currency_code: item.currency_code,
            currency_symbol: item.currency_symbol,
            currency_format: item.currency_format,
            example: item.example,
            id: item.id,
          };
        });
        setAllCurrencyData(allCurrencyData);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchDataById = async (id: any) => {
    try {
      resetWarningWage();
      setMode("view");
      const apiResponse: ApiResponse = await ratecardApis.getRatecardById(id);
      const { data, status } = apiResponse;
      if (status) {
        setOriginalData(data);
        const {
          rateCardTitle,
          validityStartDate,
          validityEndDate,
          rateCardWages,
        } = data;
        setValue("validityEndDate", dayjs(validityEndDate));
        setValue("validityStartDate", dayjs(validityStartDate));
        setValue(
          "wages",
          rateCardWages?.map((item: any) => ({
            ...item,
            id: item.wageId,
            currencyCode: item?.currency?.currencyCode,
          })) as WagesType[]
        );
        setDeletedWages([]);
        setUpdatedWages({});
      } else {
        console.log(apiResponse);
      }
    } catch (error) {
      console.log("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (mode !== "view") {
      fetchCurrencyData();
      fetchLabourTypesList();
      fetchLabourCatgoriesList();
    }
  }, [mode]);

  useEffect(() => {
    if (id) {
      fetchDataById(id);
    }
  }, [id]);

  useEffect(() => {
    if (
      validityStartDateWatch &&
      validityEndDateWatch &&
      dayjs(validityEndDateWatch).isBefore(
        dayjs(validityStartDateWatch),
        "day"
      )
    ) {
      setValue("validityEndDate", null);
    }
  }, [validityStartDateWatch]);

  const submitForm = (_formData: any) => {
    try {
      setLoading(true);
      resetWarningWage();
      const finalPayload: RatecardSubmit = {
        ..._formData,
        validityStartDate: dayjs(_formData.validityStartDate).format(
          "YYYY-MM-DD"
        ),
        validityEndDate: dayjs(_formData.validityEndDate).format("YYYY-MM-DD"),
        wages: _formData.wages.map((wage: WagesType) => {
          const wageObject: WagesType = {
            laborFlag: wage.laborFlag,
            laborTypeId: wage.laborTypeId,
            laborCategoryId: wage.laborCategoryId,
            paymentType: wage.paymentType,
            currencyId: wage.currencyId,
          };
          // if (wage.laborFlag === "Vendor Labor") {
          //   wageObject.contractorId = wage.contractorId;
          // }
          if (wage.paymentType === "Monthly") {
            wageObject.paymentDate = dayjs(wage.paymentDate).format(
              "YYYY-MM-DD"
            );
            wageObject.monthlyRate = wage.monthlyRate;
          } else if (wage.paymentType === "Daily") {
            wageObject.baseHourlyRate = wage.baseHourlyRate;
            wageObject.overTimeRate = wage.overTimeRate;
            wageObject.weekendRate = wage.weekendRate;
            wageObject.nightShiftRate = wage.nightShiftRate;
            wageObject.dayRate = wage.dayRate;
          } else if (wage.paymentType === "Hourly") {
            wageObject.baseHourlyRate = wage.baseHourlyRate;
            wageObject.overTimeRate = wage.overTimeRate;
            wageObject.weekendRate = wage.weekendRate;
            wageObject.nightShiftRate = wage.nightShiftRate;
          }
          return wageObject;
        }),
      };
      createRatecard(finalPayload);
    } catch (error) {
      console.log("error", error);
    }
  };

  const updateForm = (_formData: any) => {
    try {
      resetWarningWage();

      if (!_formData?.wages || _formData.wages.length === 0) {
        Swal.fire({
          icon: "warning",
          title: "Validation Error",
          text: "At least one labor entry is required to update the rate card.",
          confirmButtonText: "Ok",
        });
        return;
      }

      setLoading(true);
      const newWages: any = [];
      _formData?.wages?.forEach((wage: WagesType) => {
        if (!wage.wageId) {
          const wageObject: WagesType = {
            laborFlag: wage.laborFlag,
            laborTypeId: wage.laborTypeId,
            laborCategoryId: wage.laborCategoryId,
            paymentType: wage.paymentType,
            currencyId: wage.currencyId,
          };

          if (wage.paymentType === "Monthly") {
            wageObject.paymentDate = dayjs(wage.paymentDate).format(
              "YYYY-MM-DD"
            );
            wageObject.monthlyRate = wage.monthlyRate;
          } else if (wage.paymentType === "Daily") {
            wageObject.baseHourlyRate = wage.baseHourlyRate;
            wageObject.overTimeRate = wage.overTimeRate;
            wageObject.weekendRate = wage.weekendRate;
            wageObject.nightShiftRate = wage.nightShiftRate;
            wageObject.dayRate = wage.dayRate;
          } else if (wage.paymentType === "Hourly") {
            wageObject.baseHourlyRate = wage.baseHourlyRate;
            wageObject.overTimeRate = wage.overTimeRate;
            wageObject.weekendRate = wage.weekendRate;
            wageObject.nightShiftRate = wage.nightShiftRate;
          }
          newWages.push(wageObject);
        }
      });
      const finalPayload: any = {
        ratecardId: id,

        newWages,
        updatedWages: Object?.keys(updatedWages)?.map((_key: any) => {
          return {
            wageId: _key,
            ...updatedWages[_key],
          };
        }),
        deletedWages: deletedWages?.map((wageId: any) => ({
          wageId,
        })),
      };
      if (
        dayjs(originalData?.validityStartDate).format("YYYY-MM-DD") !==
        dayjs(_formData.validityStartDate).format("YYYY-MM-DD")
      ) {
        finalPayload.validityStartDate = dayjs(
          _formData.validityStartDate
        ).format("YYYY-MM-DD");
      }
      if (
        dayjs(originalData?.validityEndDate).format("YYYY-MM-DD") !==
        dayjs(_formData.validityEndDate).format("YYYY-MM-DD")
      ) {
        finalPayload.validityEndDate = dayjs(_formData.validityEndDate).format(
          "YYYY-MM-DD"
        );
      }

      console.log(finalPayload);
      updateRatecard(finalPayload);
    } catch (error) {}
  };

  const updateRatecard = async (Payload: any) => {
    try {
      console.log(CustomJsonParse(Payload, 0, 5));
      const response: ApiResponse = await ratecardApis.updateRatecard(
        CustomJsonParse(Payload, 0, 5)
      );
      const { data, status, message, error } = response || {};
      if (status) {
        Swal.fire({
          icon: "success",
          title: "Updated Successfully",
          text: data?.message,
          confirmButtonText: "Ok",
        });
        fetchDataById(id);
      } else {
        setWarningWage({
          message,
          error,
        });
      }
    } catch (error) {
      console.log("error", error);
    } finally {
      setLoading(false);
    }
  };

  const createRatecard = async (Payload: any) => {
    try {
      console.log(CustomJsonParse(Payload, 0, 5));

      const response: ApiResponse = await ratecardApis.createRatecard(
        CustomJsonParse(Payload, 0, 5)
      );
      const { data, status, message, error } = response || {};
      if (status) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: data?.message,
          confirmButtonText: "Ok",
        });
        close();
      } else {
        setWarningWage({
          message,
          error,
        });
      }
    } catch (error) {
      console.log("error", error);
    } finally {
      setLoading(false);
    }
  };

  const activateRatecard = async () => {
    try {
      const response: ApiResponse = await ratecardApis.activateRatecard(id);
      const { data, status, message, error } = response || {};
      if (status) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: data?.message,
          confirmButtonText: "Ok",
        });
        fetchDataById(id);
      } else {
        setWarningWage({
          message,
          error,
        });
      }
    } catch (error) {
      console.log("error", error);
    } finally {
      setLoading(false);
    }
  };

  const deactivateRatecard = async () => {
    try {
      const response: ApiResponse = await ratecardApis.deactivateRatecard(id);
      const { data, status, message, error } = response || {};
      if (status) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: data?.message,
          confirmButtonText: "Ok",
        });
        fetchDataById(id);
      } else {
        setWarningWage({
          message,
          error,
        });
      }
    } catch (error) {
      console.log("error", error);
    } finally {
      setLoading(false);
    }
  };

  const viewChange = () => {
    setScreenSize(window.innerWidth);
  };

  useEffect(() => {
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);
    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
    };
  }, []);

  if (loading) {
    return (
      <Flex align="center" gap="middle" className="pageLoader">
        <Spin
          indicator={<LoadingOutlined spin />}
          className="m-auto"
          size="large"
        />
      </Flex>
    );
  }
  if (!loading && !originalData && id) {
    return (
      <Result
        status="500"
        title="500"
        subTitle="Sorry, something went wrong."
        extra={
          <Button
            className="btn btn-primary btn-sm"
            type="button"
            color="primary"
            onClick={() => {
              refresh();
              close();
            }}
          >
            <Icon name={"arrow-left"} />
            <span>Back</span>
          </Button>
        }
      />
    );
  }

  return (
    <div className="mt-2">
      <div className="d-flex justify-content-between align-items-center mb-2 ">
        <h5 className="w-100 d-flex  align-items-center">
          {id ? originalData?.rateCardTitle : "New Rate Card"}
          {id ? (
            <Popconfirm
              placement="right"
              title={<></>}
              icon={<></>}
              showCancel={false}
              trigger="hover"
              okButtonProps={{
                style: {
                  display: "none",
                },
              }}
              description={
                <div className="d-flex flex-column">
                  <Descriptions
                    title=""
                    bordered
                    column={1}
                    size="small"
                    className="custom-description-title"
                  >
                    <Descriptions.Item label="Status">
                      {originalData?.status}
                    </Descriptions.Item>
                    <Descriptions.Item label="Created At">
                      {dayjs(originalData?.createdAt).format("DD-MMM-YYYY")}
                    </Descriptions.Item>
                    <Descriptions.Item label="Created By">
                      {originalData?.createdByUserName || "--"}
                    </Descriptions.Item>
                  </Descriptions>
                </div>
              }
            >
              <Icon
                name="info"
                className="ms-1"
                style={{
                  cursor: "pointer",
                }}
              />
            </Popconfirm>
          ) : (
            <></>
          )}
        </h5>
        {mode != "create" ? (
          <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2 pe-2">
            {mode == "view" && (
              <>
                <li>
                  <ViewAccess moduleName="Ratecards">
                    <UncontrolledDropdown>
                      <DropdownToggle
                        tag="a"
                        className="dropdown-toggle btn btn-icon btn-trigger"
                      >
                        <Icon name="more-v"></Icon>
                      </DropdownToggle>
                      <DropdownMenu end>
                        <ul className="link-list-opt">
                          {/* no-bdr */}
                          {mode == "view" &&
                            originalData?.status == "Active" && (
                              <li>
                                <DropdownItem
                                  tag="a"
                                  href="#tableView"
                                  onClick={(ev) => {
                                    ev.preventDefault();
                                    setMode("edit");
                                  }}
                                >
                                  <span>Edit </span>
                                </DropdownItem>
                              </li>
                            )}
                          {mode == "view" &&
                            originalData?.status == "Active" && (
                              <li>
                                <DropdownItem
                                  tag="a"
                                  href="#cardView"
                                  onClick={(ev) => {
                                    ev.preventDefault();
                                    deactivateRatecard();
                                  }}
                                >
                                  <span>Deactivate</span>
                                </DropdownItem>
                              </li>
                            )}
                          {mode == "view" &&
                            originalData?.status !== "Active" && (
                              <li>
                                <DropdownItem
                                  tag="a"
                                  href="#detailView"
                                  onClick={(ev) => {
                                    ev.preventDefault();
                                    activateRatecard();
                                  }}
                                >
                                  <span>Activate</span>
                                </DropdownItem>
                              </li>
                            )}
                        </ul>
                      </DropdownMenu>
                    </UncontrolledDropdown>
                  </ViewAccess>
                </li>
                <li>
                  <Button
                    className="btn btn-primary btn-sm"
                    type="button"
                    color="primary"
                    onClick={() => {
                      refresh();
                      close();
                    }}
                  >
                    <Icon name={"arrow-left"} />
                    <span>Back</span>
                  </Button>
                </li>
              </>
            )}
            {mode == "edit" && (
              <>
                <li>
                  <Button
                    className="btn btn-primary btn-sm"
                    type="button"
                    color="primary"
                    onClick={handleSubmit((data) => {
                      updateForm(data);
                    })}
                  >
                    <Icon name={"save"} />
                    <span>Save</span>
                  </Button>
                </li>
                <li>
                  <Button
                    className="btn btn-secondary btn-sm"
                    type="button"
                    color="secondary"
                    onClick={() => {
                      setLoading(true);
                      setMode("view");
                      resettingFields(originalData);
                    }}
                  >
                    <span>Cancel</span>
                  </Button>
                </li>
              </>
            )}
          </ul>
        ) : (
          <></>
        )}
      </div>
      <div className="nk-wizard nk-wizard-simple is-alter wizard clearfix">
        <Alert
          isOpen={originalData?.status == "Inactive"}
          color="danger"
          className="start-0 w-100 py-1 mt-2"
          style={{
            top: 0,
            textAlign: "left",
            fontSize: "14px",
            cursor: "default",
          }}
        >
          <span className="fw-bold">Inactive:</span>{" "}
          <ReactTyped
            strings={[
              `This rate card is currently inactive. Last updated on ${dayjs(
                originalData?.updatedAt
              ).format("DD-MMM-YYYY")} by "${
                originalData?.updatedByUserName || "N/A"
              }".`,
            ]}
            typeSpeed={5}
            showCursor={false}
          />
        </Alert>

        <Alert
          isOpen={warningWage?.message != undefined}
          color="danger"
          className="start-0 w-100 py-1 mt-2"
          style={{
            top: 0,
            textAlign: "left",
            fontSize: "14px",
            cursor: "default",
          }}
        >
          <span className="fw-bold">{warningWage?.error}: </span>
          <ReactTyped
            strings={[warningWage?.message || ""]}
            typeSpeed={10}
            showCursor={false}
          />
        </Alert>
        <FormProvider {...methods}>
          <form style={{ minHeight: "480px" }}>
            <div className="row g-3">
              {id ? (
                <></>
              ) : (
                <div className="col-4">
                  <FormInput
                    name="rateCardTitle"
                    label="Rate Card Title"
                    placeholder="Rate Card Title"
                    required
                    type="text"
                  />
                </div>
              )}
              <div className="col-4">
                <FormDatePicker
                  name="validityStartDate"
                  label="Validity Start Date"
                  required={true}
                  disabled={fields?.some(
                    (item: any) =>
                      item.paymentType === "Monthly" ||
                      (id &&
                        originalData?.validityStartDate &&
                        dayjs(originalData.validityStartDate).isBefore(
                          dayjs()
                        )) ||
                      mode === "view"
                  )}
                />
              </div>
              <div className="col-4">
                <FormDatePicker
                  name="validityEndDate"
                  label="Validity End Date"
                  required={true}
                  minDate={validityStartDateWatch}
                  disabled={
                    !validityStartDateWatch ||
                    fields?.some(
                      (item: any) =>
                        item.paymentType === "Monthly" || mode === "view"
                    )
                  }
                />
              </div>
            </div>
            <div className="d-flex align-items-end justify-content-between mt-4 mb-3">
              <h6 className="mb-0">Labor Wages</h6>
              <a
                onClick={() => {
                  setWageModalStatus((pre) => ({
                    ...pre,
                    open: true,
                  }));
                }}
                className="primary"
                style={{
                  fontSize: 16,
                  cursor: "pointer",
                  display: mode === "view" ? "none" : "",
                }}
              >
                <PlusSquareOutlined /> Add Labour
              </a>
            </div>
            <div
              style={{
                width: screenSize - (SideNavCollapse ? 120 : 320) + "px",
                margin: "10px auto",
              }}
            >
              <Table
                rowKey={nanoid()}
                columns={[
                  {
                    title: "Labor Flag",
                    dataIndex: "laborFlag",
                    key: "laborFlag",
                    fixed: "left",
                    width: 200,
                    render: (text: any, record: any) => {
                      return <span>{text}</span>;
                    },
                  },
                  {
                    title: "Type",
                    dataIndex: "laborTypeName",
                    key: "laborTypeName",
                    width: 150,
                  },
                  {
                    title: "Category",
                    dataIndex: "laborCategoryName",
                    key: "laborCategoryName",
                    width: 150,
                  },
                  {
                    title: "Payment Type",
                    dataIndex: "paymentType",
                    key: "paymentType",
                    width: 120,
                    render: (paymentType: any, record: any) => {
                      if (paymentType === "Daily") return <>{paymentType}</>;
                      if (paymentType === "Hourly") return <>{paymentType}</>;
                      return (
                        <span>
                          {paymentType} {" ("} Payment Date:
                          <strong>
                            {" "}
                            {dayjs(record?.paymentDate)?.format("DD-MMM-YYYY")}
                          </strong>
                          {" )"}
                        </span>
                      );
                    },
                    onCell: (record, index) => {
                      return {
                        colSpan: 1,
                      };
                    },
                  },
                  {
                    title: "Base Rate",
                    dataIndex: "baseHourlyRate",
                    key: "baseHourlyRate",
                    width: 100,
                    render: (amount: any, record: WagesType) => {
                      if (record?.paymentType === "Hourly") {
                        return (
                          <span>
                            {formatCurrency(amount, record?.currencyCode)}/hr
                          </span>
                        );
                      }
                      return "-";
                    },
                    onCell: (record) => ({
                      colSpan: 1,
                    }),
                  },
                  {
                    title: "Overtime Rate",
                    dataIndex: "overTimeRate",
                    key: "overTimeRate",
                    width: 100,
                    render: (amount: any, record: WagesType) => {
                      if (record?.paymentType === "Hourly") {
                        return (
                          <span>
                            {formatCurrency(amount, record?.currencyCode)}/hr
                          </span>
                        );
                      }
                      return "-";
                    },
                    onCell: (record) => ({
                      colSpan: 1,
                    }),
                  },
                  {
                    title: "Weekend Rate",
                    dataIndex: "weekendRate",
                    key: "weekendRate",
                    width: 100,
                    render: (amount: any, record: WagesType) => {
                      if (record?.paymentType === "Hourly") {
                        return (
                          <span>
                            {formatCurrency(amount, record?.currencyCode)}/hr
                          </span>
                        );
                      }
                      return "-";
                    },
                    onCell: (record) => ({
                      colSpan: 1,
                    }),
                  },
                  {
                    title: "Night Shift Rate",
                    dataIndex: "nightShiftRate",
                    key: "nightShiftRate",
                    width: 100,
                    render: (amount: any, record: WagesType) => {
                      if (record?.paymentType === "Hourly") {
                        return (
                          <span>
                            {formatCurrency(amount, record?.currencyCode)}/hr
                          </span>
                        );
                      }
                      return "-";
                    },
                    onCell: (record) => ({
                      colSpan: 1,
                    }),
                  },
                  {
                    title: "Day Rate",
                    dataIndex: "dayRate",
                    key: "dayRate",
                    width: 100,
                    render: (amount: any, record: WagesType) => {
                      if (record?.paymentType === "Daily") {
                        return (
                          <span>
                            {formatCurrency(amount, record?.currencyCode)}/day
                          </span>
                        );
                      }
                      return "-";
                    },
                    onCell: (record) => ({
                      colSpan: 1,
                    }),
                  },
                  {
                    title: "Monthly Rate",
                    dataIndex: "monthlyRate",
                    key: "monthlyRate",
                    width: 100,
                    render: (amount: any, record: WagesType) => {
                      if (record?.paymentType === "Monthly") {
                        return (
                          <span>
                            {formatCurrency(amount, record?.currencyCode)}/month
                          </span>
                        );
                      }
                      return "-";
                    },
                    onCell: (record) => ({
                      colSpan: 1,
                    }),
                  },
                  {
                    title: "Action",
                    key: "action",
                    fixed: "right",
                    width: 90,
                    hidden: mode === "view",
                    render: (text, record: WagesType, index) => (
                      <>
                        <Button
                          size="sm"
                          color=""
                          onClick={() => {
                            setWageModalStatus((pre) => ({
                              ...pre,
                              open: true,
                              title: "Edit Labor",
                              data: {
                                ...record,
                                paymentDate: dayjs(record?.paymentDate),
                              },
                              index: index,
                            }));
                          }}
                        >
                          <Icon name="edit" />
                        </Button>
                        <Button
                          size="sm"
                          color=""
                          onClick={() => {
                            remove(index);
                            const _key = record?.wageId;
                            if (_key) {
                              setUpdatedWages((pre: any) => {
                                let obj = { ...pre };
                                delete obj[_key];
                                return obj;
                              });
                              setDeletedWages((pre: any) => [...pre, _key]);
                            }
                          }}
                        >
                          <Icon name="trash" />
                        </Button>
                      </>
                    ),
                  },
                ]}
                dataSource={fields}
                pagination={false}
                bordered
                size="middle"
                scroll={{
                  x: 1460,
                }}
              />
            </div>

            {mode == "create" && (
              <Row className="mt-4">
                <Col lg="12">
                  <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                    <li>
                      <Button
                        className="btn btn-primary btn-md"
                        type="button"
                        color="primary"
                        onClick={handleSubmit(submitForm)}
                        disabled={fields?.length == 0}
                      >
                        Submit
                      </Button>
                    </li>
                    <li>
                      <a
                        href="#cancel"
                        onClick={(ev) => {
                          ev.preventDefault();
                          close();
                        }}
                        className="link link-light"
                      >
                        Cancel
                      </a>
                    </li>
                  </ul>
                </Col>
              </Row>
            )}
          </form>
        </FormProvider>
      </div>
      {wageModalStatus?.open ? (
        <RateCardWageModal
          open={wageModalStatus.open}
          title={wageModalStatus.title}
          mode={mode}
          append={(data: WagesType) => {
            append(data);
          }}
          update={(index: any, dirtyData: any, data: WagesType) => {
            const _key = data?.wageId;
            if (_key) {
              setUpdatedWages((pre: any) => {
                let updatedObj = pre;
                updatedObj[_key] = { ...pre[_key], ...dirtyData };
                return updatedObj;
              });
            }
            update(index, data);
          }}
          extra={{
            index: wageModalStatus.index,
            defaultData: wageModalStatus.data,
            sourceData: fields,
            currencyOptions: allCurrencyData,
            laborTypeOptions: labourTypesList,
            laborCategoryOptions: laborCatgoriesList,
            RateCardPaymentTypeOptions: RateCardPaymentTypeOptions?.filter(
              (option) => {
                if (validDays < 29 && option.value === "Monthly") return false;
                return true;
              }
            ),
          }}
          onCancel={() =>
            setWageModalStatus({
              open: false,
              title: "Add New Labor",
              data: {
                laborFlag: "My Labor",
                laborTypeId: undefined,
                laborCategoryId: undefined,
                paymentType: "Hourly",
                paymentDate: "",
                baseHourlyRate: undefined,
                overTimeRate: undefined,
                weekendRate: undefined,
                nightShiftRate: undefined,
                dayRate: undefined,
                monthlyRate: undefined,
                currencyId: 3, // INR
                currencyCode: "INR",
              },
              index: undefined,
            })
          }
        />
      ) : (
        <></>
      )}
    </div>
  );
};

export default RateCardCreation;
